<script lang="ts">
  import { debounce, difference, intersection, keys, uniq } from 'lodash-es'
  import Flash from '#app/lib/components/flash.svelte'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { onChange } from '#app/lib/svelte/svelte'
  import { getPropertiesFromWebsitesNames, getWebsitesNamesFromProperties, websitesByCategoryAndName } from '#entities/lib/entity_links'
  import type { PropertyCategory } from '#server/controllers/entities/lib/properties/properties'
  import { I18n } from '#user/lib/i18n'
  import { mainUser } from '#user/lib/main_user'
  import { updateUser } from '#user/lib/serialized_main_user'

  let {
    category,
  }: {
    category?: PropertyCategory
  } = $props()

  const { 'bibliographic-databases': bibliographicDatabases, 'web-presence': socialNetworks } = websitesByCategoryAndName

  const bibliographicDatabasesNames = keys(bibliographicDatabases)
  const socialNetworksNames = keys(socialNetworks)

  let selectedBibliographicDatabasesCount = $state(0)
  let selectedSocialNetworksCount = $state(0)

  let customProperties = mainUser?.customProperties || []
  let stringifiedSavedCustomProperties = JSON.stringify(customProperties)

  let selectedWebsites = $state(getWebsitesNamesFromProperties(customProperties))
  let flash: FlashState = $state()

  async function updateCustomProperties () {
    flash = null
    try {
      const stringifiedProperties = JSON.stringify(customProperties)
      if (stringifiedProperties !== stringifiedSavedCustomProperties) {
        stringifiedSavedCustomProperties = stringifiedProperties
        await updateUser('customProperties', customProperties)
      }
    } catch (err) {
      // Ignore duplicated requests errors, as that should most likely
      // mean the latest request sent the same state as the previous request
      if (err.statusCode !== 429) flash = err
    }
  }

  const lazyUpdate = debounce(updateCustomProperties, 1000)
  function onSelectionChange () {
    customProperties = getPropertiesFromWebsitesNames(selectedWebsites)
    selectedBibliographicDatabasesCount = intersection(bibliographicDatabasesNames, selectedWebsites).length
    selectedSocialNetworksCount = intersection(socialNetworksNames, selectedWebsites).length
    lazyUpdate()
  }
  $effect(() => {
    onChange(selectedWebsites, onSelectionChange)
  })

  function toggleAllSection ({ names, selectedCount }: { names: string[], selectedCount: number }) {
    if (selectedCount !== names.length) {
      selectedWebsites = uniq(selectedWebsites.concat(names))
    } else {
      selectedWebsites = difference(selectedWebsites, names)
    }
  }
</script>

{#if category == null || category === 'bibliographic-databases'}
  <section>
    <label class="category">
      <input
        type="checkbox"
        checked={selectedBibliographicDatabasesCount === bibliographicDatabasesNames.length}
        indeterminate={selectedBibliographicDatabasesCount !== 0 && selectedBibliographicDatabasesCount !== bibliographicDatabasesNames.length}
        onclick={() => toggleAllSection({ names: bibliographicDatabasesNames, selectedCount: selectedBibliographicDatabasesCount })}
      />
      {I18n('bibliographic databases')}
    </label>
    <fieldset>
      {#each bibliographicDatabasesNames as websiteName (websiteName)}
        <label>
          <input type="checkbox" bind:group={selectedWebsites} value={websiteName} />
          {websiteName}
        </label>
      {/each}
    </fieldset>
  </section>
{/if}

{#if category == null || category === 'web-presence'}
  <section>
    <label class="category">
      <input
        type="checkbox"
        checked={selectedSocialNetworksCount === socialNetworksNames.length}
        indeterminate={selectedSocialNetworksCount !== 0 && selectedSocialNetworksCount !== socialNetworksNames.length}
        onclick={() => toggleAllSection({ names: socialNetworksNames, selectedCount: selectedSocialNetworksCount })}
      />
      {I18n('social networks')}
    </label>
    <fieldset>
      {#each socialNetworksNames as websiteName (websiteName)}
        <label>
          <input type="checkbox" bind:group={selectedWebsites} value={websiteName} />
          {websiteName}
        </label>
      {/each}
    </fieldset>
  </section>
{/if}

<Flash bind:state={flash} />

<style lang="scss">
  @import "general/scss/utils";
  section{
    margin-block-end: 1em;
    @include shy-border;
    @include radius;
  }
  fieldset{
    columns: 15rem auto;
  }
  .category{
    font-weight: bold;
    font-size: 1rem;
    padding: 0.5em;
    background-color: $lighter-grey;
  }
  label:not(.category){
    font-size: 1rem;
    @include display-flex(row, center);
    padding: 0.5em;
  }
</style>
